import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { FinanceApiService, GeographicAreaRecord } from '../finance/finance-api.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';

type LevelOption = { value: string; labelKey: string };

@Component({
  standalone: true,
  selector: 'ain-platform-geography-areas',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule, SelectModule],
  template: `
    <ain-page [title]="t('platform.geography.title')" [description]="t('platform.geography.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('platform.geography.action.createCountry')" (click)="openCreate('Country')"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          <div class="filters">
            <label>
              <span>{{ t('sales.territories.fields.code') }}</span>
              <input pInputText type="text" [(ngModel)]="filters.code" (ngModelChange)="applyFilters()" />
            </label>
            <label>
              <span>{{ t('sales.territories.fields.name') }}</span>
              <input pInputText type="text" [(ngModel)]="filters.name" (ngModelChange)="applyFilters()" />
            </label>
            <label>
              <span>{{ t('sales.territories.fields.level') }}</span>
              <p-select [options]="levelOptions" optionValue="value" [(ngModel)]="filters.level" [showClear]="true" appendTo="body" (ngModelChange)="load()">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
          </div>
          <p-table [value]="rows()" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('sales.territories.fields.code') }}</th>
                <th>{{ t('sales.territories.fields.name') }}</th>
                <th>{{ t('sales.territories.fields.level') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.code }}</td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.name }}</button></td>
                <td>{{ levelLabel(row.level) }}</td>
                <td>{{ row.isActive ? t('sales.territories.active') : t('sales.territories.inactive') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .filters { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; }
    .filters label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
  `]
})
export class PlatformGeographyAreasComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly sourceRows = signal<GeographicAreaRecord[]>([]);
  readonly rows = signal<GeographicAreaRecord[]>([]);
  readonly filters = { code: '', name: '', level: 'Country' as string | null };

  readonly levelOptions: LevelOption[] = [
    { value: 'Country', labelKey: 'platform.geography.levels.country' },
    { value: 'Province', labelKey: 'platform.geography.levels.province' },
    { value: 'City', labelKey: 'platform.geography.levels.city' },
    { value: 'Area', labelKey: 'platform.geography.levels.area' },
    { value: 'Route', labelKey: 'platform.geography.levels.route' }
  ];

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    const query = this.filters.level
      ? { level: this.filters.level }
      : {};
    this.api.listGeographicAreas(query).subscribe({
      next: items => {
        this.sourceRows.set(items);
        this.applyFilters();
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  applyFilters(): void {
    const code = this.filters.code.trim().toLowerCase();
    const name = this.filters.name.trim().toLowerCase();
    this.rows.set(
      this.sourceRows().filter(row =>
        (!code || row.code.toLowerCase().includes(code)) &&
        (!name || row.name.toLowerCase().includes(name))
      )
    );
  }

  openCreate(level: string): void {
    void this.router.navigate(['/platform/geography/create'], { queryParams: { level } });
  }

  openDetail(id: string): void {
    void this.router.navigate(['/platform/geography', id]);
  }

  levelLabel(level: string): string {
    const key = `platform.geography.levels.${level.toLowerCase()}`;
    return this.localization.translate(key);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
